import {
	Table,
	Stack,
	Button,
	Component,
	Inline,
	Icons,
	Text,
	Box,
	Heading,
	Dropdown,
} from "@sembark-travel/ui/base"
import {
	areAdvancedFiltersAppliedDefault,
	CursorListView,
	Search,
	TSearchParams,
	useSearch,
} from "@sembark-travel/ui/list"
import { useLocationQuery } from "@sembark-travel/ui/router"
import { useXHR } from "@sembark-travel/xhr"
import { useEffect, useState } from "react"
import { Dialog } from "@sembark-travel/ui/dialog"
import {
	CheckboxInputField,
	Form,
	GetFieldValue,
	isTruthy,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	withServerErrors,
} from "@sembark-travel/ui/form"
import { Optional } from "utility-types"
import {
	LatLongInputFields,
	MapLink,
	SynonumsNamesInputField,
} from "./FormInputFields"
import {
	worldXHR,
	TWorldAirport,
	TWorldCountry,
	TWorldState,
	TWorldCity,
	TWorldTimezone,
} from "../../World"
import {
	SelectWorldCities,
	SelectWorldCountries,
	SelectWorldStates,
	SelectWorldTimezones,
} from "../../World/List"
import { showSnackbar } from "@sembark-travel/ui/snackbar"

type TFilters = TSearchParams & {
	unverified_only?: 1
}

type TFiltersInQuery = TSearchParams & {
	unverified_only?: 1
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const { unverified_only, ...others } = filters
	const query: TFiltersInQuery = { ...others }
	if (isTruthy(unverified_only)) {
		query.unverified_only = 1
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const { unverified_only, ...others } = query
	const filters: TFilters = { ...others }
	if (isTruthy(unverified_only)) {
		filters.unverified_only = 1
	}
	return filters
}
function Filters() {
	return (
		<Stack>
			<SwitchInputField name="unverified_only" label="Unverified Only" />
		</Stack>
	)
}
export function AirportsList({ country }: { country?: TWorldCountry }) {
	const xhr = useXHR()
	const [query, setQuery] = useLocationQuery({
		fromQuery: queryToFilters,
		toQuery: filtersToQuery,
	})
	const [searchParams, setSearchParams] = useSearch(query)
	useEffect(() => {
		setQuery(searchParams)
	}, [searchParams, setQuery])
	return (
		<Search
			title="Airports"
			initialParams={searchParams}
			onSearch={(params) => setSearchParams({ ...params, cursor: null })}
			Filters={Filters}
			resetParams={{
				q: "",
				page: 1,
			}}
			areAdvancedFiltersApplied={(filters) => {
				const { unverified_only, ...otherFilters } = filters
				return (
					isTruthy(unverified_only) ||
					areAdvancedFiltersAppliedDefault(otherFilters)
				)
			}}
			actions={() => (
				<Component initialState={false}>
					{({ state, setState }) => (
						<>
							<Button onClick={() => setState(true)}>New Airport</Button>
							<Dialog
								open={state}
								onClose={() => setState(false)}
								title="Add New Airport"
							>
								<Dialog.Body>
									<AddNewAirport
										country={country}
										onCancel={() => setState(false)}
										onSuccess={() => setState(false)}
									/>
								</Dialog.Body>
							</Dialog>
						</>
					)}
				</Component>
			)}
		>
			{() => (
				<CursorListView<TWorldAirport>
					pageKey={`admin/world/airports-cursor`}
					params={searchParams}
					onCursorChange={(cursor) =>
						setSearchParams({ ...searchParams, cursor })
					}
					fetch={(xhr, params) =>
						worldXHR(xhr).getAirportsWithPagination({
							country_id: country?.id,
							...params,
							include: "city,state,country,timezone",
						})
					}
				>
					{({ items, refresh }) => (
						<Table
							headers={[
								"ICAO",
								"IATA",
								"Name",
								"City",
								"State",
								"Country",
								"Timezone",
								"Map",
								"",
							]}
							responsive
							hover
							bordered
							alignCols={{ 8: "right" }}
							rows={items.map((item) => [
								item.icao,
								item.iata,
								<Stack>
									<Text>{item.name}</Text>
									{[item.synonyms_1, item.synonyms_2, item.synonyms_3]
										.filter(Boolean)
										.map((a) => (
											<Text key={a} fontSize="sm" color="muted">
												{a}
											</Text>
										))}
								</Stack>,
								item.city?.name,
								item.state?.name,
								item.country?.name,
								item.timezone?.name,
								<MapLink lat={item.latitude} long={item.longitude} />,

								<Dropdown alignRight>
									<Dropdown.ToggleButton level="tertiary" size="sm">
										<Icons.DotsVertical />
									</Dropdown.ToggleButton>
									<Dropdown.Menu>
										<Component initialState={false}>
											{({ state, setState }) => (
												<>
													<Dropdown.MenuItem onClick={() => setState(true)}>
														<Icons.Pencil /> Edit
													</Dropdown.MenuItem>
													<Dialog
														open={state}
														onClose={() => setState(false)}
														title={`Edit Airport "${item.name}"`}
													>
														<Dialog.Body>
															<EditAirport
																airport={item}
																onCancel={() => setState(false)}
																onSuccess={() => {
																	refresh()
																	setState(false)
																}}
															/>
														</Dialog.Body>
													</Dialog>
												</>
											)}
										</Component>
										<Dropdown.MenuItem
											onClick={() => {
												if (
													window.confirm(
														"Are you sure you want to delete this airport ?."
													)
												) {
													worldXHR(xhr)
														.destroyAirport(item.id)
														.then((resp) => {
															showSnackbar(
																resp || "Airport deleted successfully."
															)
															refresh()
														})
														.catch((e) => {
															const error: Error = e
															window.alert(
																error.message || "Something went wrong"
															)
														})
												}
											}}
										>
											<Icons.Trash /> Delete
										</Dropdown.MenuItem>
									</Dropdown.Menu>
								</Dropdown>,
							])}
						/>
					)}
				</CursorListView>
			)}
		</Search>
	)
}

function AddNewAirport({
	country,
	onSuccess,
	...props
}: Omit<NewAirportFormProps, "onSubmit"> & { country?: TWorldCountry }) {
	const xhr = useXHR()
	return (
		<NewAirportForm
			initialValues={{ country }}
			onSubmit={(payload) =>
				worldXHR(xhr).storeAirport(payload).then(onSuccess)
			}
			{...props}
		/>
	)
}

function EditAirport({
	airport,
	onSuccess,
	...props
}: Omit<NewAirportFormProps, "onSubmit" | "stateId"> & {
	airport: TWorldAirport
}) {
	const xhr = useXHR()
	return (
		<NewAirportForm
			tenantId={airport.tenant_id}
			initialValues={{
				icao: airport.icao,
				iata: airport.iata,
				name: airport.name,
				synonyms_1: airport.synonyms_1,
				synonyms_2: airport.synonyms_2,
				synonyms_3: airport.synonyms_3,
				latitude: airport.latitude,
				longitude: airport.longitude,
				elevation: airport.elevation,
				country: airport.country,
				state: airport.state,
				city: airport.city,
				timezone: airport.timezone,
			}}
			onSubmit={(payload) =>
				worldXHR(xhr).updateAirport(airport.id, payload).then(onSuccess)
			}
			{...props}
		/>
	)
}
type NewAirportFormData = {
	icao?: string
	iata?: string
	name?: string
	synonyms_1?: string
	synonyms_2?: string
	synonyms_3?: string
	latitude?: string
	longitude?: string
	elevation?: string
	country?: TWorldCountry
	state?: TWorldState
	city?: TWorldCity
	timezone?: TWorldTimezone
	mark_verified?: 0 | 1
}

type NewAirportFormProps = {
	onSubmit: (payload: object) => Promise<unknown>
	onSuccess?: () => void
	onCancel: () => void
	initialValues?: Optional<NewAirportFormData>
	tenantId?: number
}

function NewAirportForm({
	onSubmit,
	onCancel,
	initialValues: propInitialValues,
	tenantId,
}: NewAirportFormProps) {
	const [initialValues] = useState<NewAirportFormData>(() => ({
		name: "",
		iata: "",
		icao: "",
		synonyms_1: "",
		synonyms_2: "",
		synonyms_3: "",
		latitude: "",
		longitude: "",
		elevation: "",
		...propInitialValues,
	}))
	return (
		<Form<NewAirportFormData>
			initialValues={initialValues}
			onSubmit={withServerErrors(
				async ({
					country,
					state,
					city,
					timezone,
					mark_verified,
					...values
				}) => {
					await onSubmit({
						...values,
						country: country?.id,
						state: state?.id,
						city: city?.id,
						timezone: timezone?.id,
						mark_verified: tenantId ? (mark_verified ? 1 : 0) : undefined,
					})
				}
			)}
			subscription={{ submitting: true }}
		>
			{({ submitting, handleSubmit }) => (
				<form onSubmit={handleSubmit} noValidate>
					<Stack gap="4">
						<Inline gap="4" flexWrap="wrap">
							<TextInputField
								name="icao"
								type="text"
								label="ICAO"
								required
								placeholder="Four-letter Code"
								style={{ maxWidth: "100px" }}
							/>
							<TextInputField
								name="iata"
								type="text"
								label="IATA"
								placeholder="Three-letter GeoCode"
								style={{ maxWidth: "100px" }}
								secondaryLabel="optional"
							/>
							<Box flex="1">
								<TextInputField
									name="name"
									type="text"
									label="Name"
									required
									placeholder="e.g. Jaipur Internation Airport"
								/>
							</Box>
						</Inline>
						<SynonumsNamesInputField />
						<Stack gap="1">
							<Heading>Location</Heading>
							<Text color="muted">Provide ariport's city/state/countries</Text>
						</Stack>
						<Inline gap="4" flexWrap="wrap">
							<SelectField
								select={SelectWorldCountries}
								name="country"
								label="Country"
							/>
							<GetFieldValue<TWorldCountry | undefined> name="country">
								{({ value }) =>
									!value ? null : (
										<SelectField
											select={SelectWorldStates}
											countryId={value.id}
											name="state"
											label="State"
											secondaryLabel="optional"
										/>
									)
								}
							</GetFieldValue>
							<GetFieldValue<TWorldState | undefined> name="state">
								{({ value }) =>
									!value ? null : (
										<SelectField
											select={SelectWorldCities}
											stateId={value.id}
											name="city"
											label="City"
											secondaryLabel="optional"
										/>
									)
								}
							</GetFieldValue>
							<GetFieldValue<TWorldCountry | undefined> name="country">
								{({ value }) =>
									!value ? null : (
										<SelectField
											select={SelectWorldTimezones}
											countryId={value.id}
											name="timezone"
											label="Timezone"
											fetchOnMount
											secondaryLabel="optional"
										/>
									)
								}
							</GetFieldValue>
						</Inline>
						<Inline flexWrap="wrap" gap="4">
							<LatLongInputFields />
							<TextInputField
								name="elevation"
								label="Elevation"
								secondaryLabel="optional"
								type="text"
								style={{ maxWidth: "100px" }}
							/>
						</Inline>
						{tenantId ? (
							<Box>
								<CheckboxInputField
									name="mark_verified"
									label={"Mark as verified"}
									type="checkbox"
								/>
							</Box>
						) : null}

						<SubmissionError />
						<Inline gap="4">
							<Button type="submit" disabled={submitting}>
								{submitting ? "Please wait.." : "Save Details"}
							</Button>
							<Button disabled={submitting} onClick={() => onCancel()}>
								Cancel
							</Button>
						</Inline>
					</Stack>
				</form>
			)}
		</Form>
	)
}
